import { EmptyState } from '@/components/ui/empty-state';
import {
  useEmailConnect,
  type EmailConnectProvider,
} from '@/components/integrations/use-email-connect';
import { Button, HStack, Image, RadioCard, Text, VStack } from '@chakra-ui/react';
import { PlatformEnum } from '@fluenceur/types';
import { Mail } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EmailConnectPromptProps {
  /** Heading; defaults to the bulk-email "no account connected" copy. */
  title?: string;
  /** Sub-copy; defaults to the bulk-email copy. */
  description?: string;
  /** When set, the connect button is disabled and this sentence says why. */
  disabledReason?: string;
  /** In-app path the OAuth redirect comes back to; the backend default otherwise. */
  returnTo?: string;
}

/**
 * "Connect your mailbox" prompt: a Gmail/Outlook provider picker that kicks off
 * the OAuth redirect. Self-contained — it owns the provider selection and the
 * redirect, so any surface that needs a connected mailbox can drop it in.
 * Generalized from the bulk-email flow; copy defaults to the bulk-email keys so
 * existing consumers keep working, while new surfaces pass their own copy.
 * Borders + background contrast only, no shadow.
 */
export const EmailConnectPrompt = ({
  title,
  description,
  disabledReason,
  returnTo,
}: EmailConnectPromptProps) => {
  const { t } = useTranslation();
  const { connect, isConnecting: isLoading } = useEmailConnect(returnTo);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const handleConnect = async () => {
    if (!selectedProvider || disabledReason) return;
    await connect(selectedProvider as EmailConnectProvider);
  };

  return (
    <EmptyState
      title={title ?? t('bulkEmail.modal.notConnected.title')}
      description={description ?? t('bulkEmail.modal.notConnected.description')}
      icon={<Mail width={28} height={28} />}
      bare
      py={6}
      px={0}
    >
      <VStack gap="3" w="full">
        <RadioCard.Root
          orientation="vertical"
          align="center"
          value={selectedProvider}
          onValueChange={(e) => setSelectedProvider(e.value)}
          w="full"
        >
          <HStack w="full" justify="center" gap="3">
            <RadioCard.Item value={PlatformEnum.Gmail} w="128px" cursor="pointer" rounded="lg">
              <RadioCard.ItemHiddenInput />
              <RadioCard.ItemControl aspectRatio="1" alignItems="center" justifyContent="center">
                <VStack gap="2" textAlign="center">
                  <Image src="/images/integrations/gmail.svg" boxSize="10" />
                  <RadioCard.ItemText lineHeight="1">Gmail</RadioCard.ItemText>
                </VStack>
              </RadioCard.ItemControl>
            </RadioCard.Item>
            <RadioCard.Item value={PlatformEnum.Outlook} w="128px" cursor="pointer" rounded="lg">
              <RadioCard.ItemHiddenInput />
              <RadioCard.ItemControl aspectRatio="1" alignItems="center" justifyContent="center">
                <VStack gap="2" textAlign="center">
                  <Image src="/images/integrations/outlook.svg" boxSize="10" />
                  <RadioCard.ItemText lineHeight="1">Outlook</RadioCard.ItemText>
                </VStack>
              </RadioCard.ItemControl>
            </RadioCard.Item>
          </HStack>
        </RadioCard.Root>

        <Button
          colorPalette="primary"
          size="md"
          onClick={handleConnect}
          loading={isLoading}
          disabled={!selectedProvider || isLoading || Boolean(disabledReason)}
          w="full"
        >
          {t('bulkEmail.modal.notConnected.connect')}
        </Button>
        {disabledReason && (
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {disabledReason}
          </Text>
        )}
      </VStack>
    </EmptyState>
  );
};
